import type { AccessGates, AccessInfo } from './use-access'
import type { ModuleKey } from './permissions'

/**
 * The access-relevant fields of a sidebar item. Structural on purpose:
 * `shared/` must not import `app/navigation`, and `NavItem` satisfies this.
 */
export interface NavAccessFields {
  module?: ModuleKey
  access?: keyof AccessGates
  superadminOnly?: boolean
  editAllOnly?: boolean
  alsoRequires?: ModuleKey
  hiddenWhen?: ModuleKey
}

/**
 * Sidebar item visibility — extracted verbatim from `Sidebar` so the product
 * walkthrough can ask "is this group visible?" with the same answer the
 * sidebar renders. `has3d` is `useAuthStore.hasPermission3d`.
 *
 * NOTE: this is the SIDEBAR rule, which for a few items is weaker than the
 * route guard (Prompt Library, Configurations, System Admin). Decide whether a
 * user may be SENT to a route with `isAccessAllowed` + the route's rule.
 */
export function isNavItemVisible(
  item: NavAccessFields,
  access: AccessInfo,
  has3d: (module: string, action: string, scope?: string) => boolean,
): boolean {
  if (item.superadminOnly && !has3d('system_admin', 'edit', 'all')) return false
  if (item.editAllOnly && item.module && !has3d(item.module, 'edit', 'all')) return false
  // One destination, two homes: `alsoRequires` / `hiddenWhen` keep the
  // same path from showing under two group labels at once.
  if (item.alsoRequires && !access.can(item.alsoRequires, 'read')) return false
  if (item.hiddenWhen && access.can(item.hiddenWhen, 'read')) return false
  // Prefer the post-refactor `module` field; fall back to the legacy named gate.
  if (item.module) return access.can(item.module, 'read')
  if (item.access) return Boolean(access[item.access])
  return true
}

/** Deterministic walkthrough anchor per sidebar group: "Master Library" → "master-library". */
export function navGroupSlug(label: string): string {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
}
